import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  effect,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import {
  AuthenticationResult,
  AuthError,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { ApmBase } from "@elastic/apm-rum";
import { ApmService } from "@elastic/apm-rum-angular";
import {
  NgbModal,
  NgbModalOptions,
  NgbModalRef,
} from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import * as moment from "moment";
import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { filter, finalize, takeUntil } from "rxjs/operators";
import * as envModule from "../environments/environment";
import { EvaState } from "../libraries/e3duiplatform-eva-mfe/libs/eva/src/lib/ai-agent/models/eva-state.model";
import { AIAgentService } from "../libraries/e3duiplatform-eva-mfe/libs/eva/src/lib/ai-agent/services/ai-agent.service";
import { PendoAnalyticsService } from "./core/analytics/pendo-analytics.service";
import { USERROLES } from "./app.config";
import { AppEnvConfig } from "./app.env.config";
import { ElasticApmCustomLabelService } from "./core/analytics/elasticAPM-custom-labels.service";
import { ElasticApmRouteTrackingService } from "./core/analytics/elasticAPM-route-tracking.service";
import { IdleSessionService } from "./core/idle/idle-session.service";
import { IdleSessionPhase } from "./core/idle/idle-session.types";
import { LocalCacheService } from "./core/local-cache/local-cache.service";
import { SubheaderHandlerService } from "./core/nav/subheader-handler.service";
import { MsalV3Service } from "./core/services/msal-V3.service";
import { UserService } from "./core/user/user.service";
import { MenuService } from "./menu/menu.service";
import { User } from "./shared-main/models/_models";
import { ISubheaderLeftSection } from "./shared/components/sub-header/sub-header.modal";
import { ScriptLoaderService } from "./shared/services/script-loader.service";

const hideSubHeaderRoutes = [
  "/financial-impact",
  "/action-recommendations",
  "/home",
  "/data-visualization-v2",
  "/omni-iq",
  "/condenseriq",
  "/heat-exchanger-iq",
  "/rolling-mills-iq",
  "/air-separation-iq",
  "/caster-systems-iq",
  "/chiller-iq",
  "/operational-reliability-iq",
  "/water",
  "/legionella-test-results",
  "/upload-reports-and-plans",
  "/coolant-health-iq",
  "/e-data-entry"
];
@Component({
  selector: "nalco-root",
  templateUrl: "./app.component.html",
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  loading: Boolean = false;
  location!: string;
  hideLayout: boolean = false;
  user!: User;
  isSubHeaderHidden: boolean = false;
  isMobile: Boolean = false;
  private readonly mobileBreakpoint = 720;
  isUserInfoAvailable: boolean = false;
  userInfoCache: any;
  id_token!: string;
  allannouncement: any[] = [];
  chachedAllAnnouncemnets: any;
  private subscriptions: Subscription[] = [];
  // Holds the customer hierarchy selected in the global search filters, reported to Pendo as the account.
  private readonly pendoAnalyticsService = inject(PendoAnalyticsService);
  private alive: boolean;
  private globalIdleEnabled = true;
  height!: number;
  screenWidth!: number;
  idleStateKey = "IDLE_TIMEOUT_POPUP.IDLE_INITIAL_STATE_TEXT";
  timedOut = false;
  lastPing: Date | null = null;
  idleCountFormatted = "";
  idleModalRef: NgbModalRef | null = null;
  private readonly idleViewReady = signal(false);
  @ViewChild("idleModalContent", { read: TemplateRef, static: false })
  idleModalContent!: TemplateRef<any>;
  isIframe = false;
  isloggedIn = false;
  isCloudAccessChecked = false;
  private readonly _destroying$ = new Subject<void>();
  userInfoDetails = new BehaviorSubject<any>(null);
  $userInfoDetails = this.userInfoDetails.asObservable();
  hideSubHeader: boolean = false;
  hideFooter: boolean = false;
  isFbiOnboardingRoute: boolean = false;
  isWaterIntelligenceRoute: boolean = false;
  isChillerIqRoute: boolean = false;
  isFnbiRoute: boolean = false;
  isDCI = false;
  isExpand = false;
  isHomePage = false;
  showLoader = false;
  isEvaOpen = signal(false); // Track if EVA agent is open
  isEvaCollapsed = signal(false);
  isEvaSnowEnabled = !!(envModule.environment as any).eva_snow?.enabled;
  hasEngagementPermission = false;
  leftSection!: ISubheaderLeftSection; // Getting page information from the existing sub header service
  private scriptPath =
    "https://ecolabqa.service-now.com/scripts/sn_csm_ec.js?v=5.6";
  private settings = {
    ECX_Module_Id__c:
      "https://ecolabqa.service-now.com/#17f65312474796901f7634d4116d43a4",
  };
  idToken: string | null = null;
  private apmInstance?: ApmBase;
  constructor(
    private translate: TranslateService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private config: AppEnvConfig,
    private localCacheService: LocalCacheService,
    private userService: UserService,
    private idleModal: NgbModal,
    private authService: MsalService,
    private msalV3Service: MsalV3Service,
    private msalBroadcastService: MsalBroadcastService,
    private menuService: MenuService,
    private subHeaderService: SubheaderHandlerService,
    private scriptLoader: ScriptLoaderService,
    private elasticAPMService: ApmService,
    private elasticAPMRouteTrackingService: ElasticApmRouteTrackingService,
    private elasticAPMCustomLabelsService: ElasticApmCustomLabelService,
    private cdref: ChangeDetectorRef,
    private aiAgentService: AIAgentService,
    private idleSessionService: IdleSessionService,
  ) {
    this.userInfoCache = JSON.parse(
      this.localCacheService.fetchData("msalUserInfo"),
    );
    if (this.localCacheService.fetchData("returnUrl") === null) {
      const urlHash = new URL(window.location.href).hash;
      const redirectURL =
        new URLSearchParams(urlHash.split("?")[1]).get("returnUrl") ||
        window.location.hash.slice(1);
      if (redirectURL.indexOf("code") < 0) {
        this.localCacheService.storeData("returnUrl", redirectURL);
      }
    }
    this.loading = true;
    this.alive = true;

    // sets the ping interval to 15 seconds
    // this.keepalive.interval(IdleTimeoutConfig.setKeepAliveInterval);

    // this.subscriptions.push(
    //   this.keepalive.onPing.subscribe(() => (this.lastPing = new Date()))
    // );

    effect(() => {
      const phase = this.idleSessionService.phase();
      const countdown = this.idleSessionService.countdownSeconds();
      const warningUiEnabled = this.idleSessionService.warningUiEnabled();
      const viewReady = this.idleViewReady();

      this.idleStateKey = this.getIdleMessageKey(phase);
      this.timedOut = phase === "timedOut";
      this.idleCountFormatted =
        phase === "warning"
          ? moment.utc(countdown * 1000).format("mm:ss")
          : "";

      if (phase === "warning" && warningUiEnabled && viewReady) {
        this.openIdleModal();
      } else {
        this.closeIdleModal();
      }

      if (phase === "timedOut") {
        this.showLoader = false;
      }

      this.cdref.markForCheck();
    });

    if (envModule.environment.enableElasticApm) {
      // Agent API is exposed through this apm instance
      this.apmInstance = this.elasticAPMService.init({
        serviceName: envModule.environment.elasticApmConfig?.serviceName,
        serverUrl: envModule.environment.elasticApmConfig?.serverUrl,
        environment: envModule.environment.environment,
        eventsLimit: 400,
      });
    }
  }

  ngAfterViewInit() {
    this.idleViewReady.set(true);
  }

  ngOnInit() {
    this.updateOnboardingRouteFlag();
    // Get the page route name
    this.getPagetRoute();
    this.authService.initialize().subscribe(() => {
      // if no signed-in account found will redirect back to sign-in authority page
      this.msalBroadcastService.msalSubject$
        .pipe(
          // Optional filtering of events
          filter(
            (msg: EventMessage) =>
              msg.eventType === EventType.ACCOUNT_ADDED ||
              msg.eventType === EventType.ACCOUNT_REMOVED,
          ),
          takeUntil(this._destroying$),
        )
        .subscribe((result: EventMessage) => {
          if (this.authService.instance.getAllAccounts().length === 0) {
            if (this.idleSessionService.isLogoutInProgressValue()) {
              return false;
            }
            this.msalV3Service.clearMSALCache();
            this.authService.loginRedirect();
            return false;
          }
          if (result.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
            console.log(result.payload);
            return false;
          }
        });
      this.msalBroadcastService.inProgress$
        .pipe(
          filter(
            (status: InteractionStatus) => status === InteractionStatus.None,
          ),
          takeUntil(this._destroying$),
        )
        .subscribe(() => {
          this.isloggedIn =
            this.authService.instance.getAllAccounts().length > 0;
          this.idToken = this.msalV3Service.getIdToken();
          if (this.isloggedIn) {
            this.idleSessionService.startSession(
              this.msalV3Service.getIdleSessionKey(),
            );
            this.CheckCloudEnvAccessAndRedirect();
            console.log("InteractionStatus: ", InteractionStatus.None); //required for debugging
          }
        });
      // login success event
      this.msalBroadcastService.msalSubject$
        .pipe(
          filter(
            (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS,
          ),
        )
        .subscribe((result: EventMessage) => {
          const payload = result.payload as AuthenticationResult;
          this.authService.instance.setActiveAccount(payload.account);
          this.idleSessionService.resetSessionState(
            payload.account?.homeAccountId ||
              payload.account?.localAccountId ||
              payload.account?.username,
          );
          console.log("login-success", result); //required for debugging
        });
      //  login failure event
      this.msalBroadcastService.msalSubject$
        .pipe(
          // Optional filtering of events
          filter(
            (msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE,
          ),
          takeUntil(this._destroying$),
        )
        .subscribe((result: EventMessage) => {
          if (
            result.error instanceof AuthError ||
            result.error instanceof InteractionRequiredAuthError
          ) {
            // Do something with the error
            this.msalV3Service.clearMSALCache();
            this.authService.loginRedirect();
          }
        });
      this.msalV3Service.authInit();
    });
    this.translate.addLangs(this.config.getEnv("languages"));
    this.translate.setFallbackLang("en");
    this.translate.use(this.userService.getUserCulture());
    this.subscribeToUserInfoDetails();
    this.subscriptions.push(
      this.menuService.navExpand.subscribe((value) => {
        if (JSON.parse(this.config.getEnv("isNewUIEnabled").toString())) {
          this.isExpand = value;
        }
      }),
    );
    // Subscribe to EVA state changes to track open/close status
    this.subscriptions.push(
      this.aiAgentService.getEvaState$().subscribe((state: EvaState) => {
        this.isEvaOpen.set(state.show || false);
        this.isEvaCollapsed.set(state.collapsed || false);
        // this.cdref.markForCheck();
      }),
    );
  }

  CheckCloudEnvAccessAndRedirect() {
    // Skip cloud access API call check for scheduled PDF generation
    const returnUrl = this.localCacheService.fetchData("returnUrl");
    const urlHasPdfGeneration =
      returnUrl?.includes("pdfGeneration=true") ?? false;
    if (urlHasPdfGeneration) {
      this.isCloudAccessChecked = true;
      return;
    }
    console.log("checking cloud access");
    this.userService
      .getUserCloudAccess()
      .pipe(
        finalize(() => {
          console.log("completed cloud access check");
          this.isCloudAccessChecked = true;
        }),
      )
      .subscribe((access: number) => {
        let cloudAccess: number;
        cloudAccess = access;
        console.log("cloudAccess value" + cloudAccess);
        const app1Access = cloudAccess != 2;
        const app1host = envModule.environment.cloudEnv === "azure";
        console.log("app1Access", app1Access);
        console.log("app1host", app1host);
        const manualRedirectionValue = document.cookie
          .split("; ")
          .find((row) => row.startsWith("manualredirection="))
          ?.split("=")[1];

        console.log("ManualRedirectionValue", manualRedirectionValue);
        if (!manualRedirectionValue || manualRedirectionValue !== "true") {
          console.log("ManualRedirectionValue is not set or not true");
          if (app1Access && !app1host) {
            this.showLoader = true;
            this.hideLayout = true;
            console.log("Setting cookie and reloading for app1");
            document.cookie = "host=/app1; path=/;Secure";
            window.location.href = `${window.location.origin}`;
            return;
          }
          if (!app1Access && app1host) {
            this.showLoader = true;
            this.hideLayout = true;
            console.log("Setting cookie and reloading for app2");
            document.cookie = "host=/app2; path=/;Secure";
            window.location.href = `${window.location.origin}`;
            return;
          }
        }
        this.cdref.markForCheck();
      });
  }

  /**
   * @description - Handles the change in subheader visibility by updating the 'isSubHeaderHidden' property.
   * @param hidden - A boolean value indicating whether the subheader is hidden(true) or not(false).
   * @returns - This method does not return a value.
   */
  onSubHeaderHiddenChange(hidden: boolean) {
    this.isSubHeaderHidden = hidden;
  }

  /**
   * Subscribes to user information details and handles the user data when available.
   *
   * This method subscribes to the user information details from the user service. When user data is available,
   * it updates the user information cache, handles the user data, and sets the user context for Elastic APM.
   * Additionally, it starts a custom Labels for measuring internet speed using the Elastic APM service.
   *
   * @memberof AppComponent
   */
  subscribeToUserInfoDetails() {
    this.subscriptions.push(
      this.userService.$userInfoDetails.subscribe(
        (userData) => {
          this.userInfoCache = JSON.parse(
            this.localCacheService.fetchData("msalUserInfo"),
          );
          if (userData !== null || this.userInfoCache !== null) {
            this.handleUserDataAvailable();
            const apmInstance = this.apmInstance;
            if (envModule.environment.enableElasticApm && apmInstance) {
              apmInstance.setUserContext({
                username:
                  this.userInfoCache?.FirstName +
                  " " +
                  this.userInfoCache?.LastName,
                email: this.userInfoCache?.EmailAddress,
              });
              this.elasticAPMRouteTrackingService.trackRouteChanges(
                apmInstance,
              );
              this.elasticAPMCustomLabelsService.addCustomLabels(
                this.userInfoCache,
                apmInstance,
              );
            }
          } else {
            this.isUserInfoAvailable = false;
          }
        },
        (error) => {
          // receiving the error to show appropriate message to user if there is no access to application
          if (error.status === 417) {
            this.isUserInfoAvailable = false;
            // this.translate.addLangs(this.config.getEnv('languages'));
            // this.translate.setFallbackLang(this.userService.getUserCulture());
            this.loading = false;
          }
        },
      ),
    );
  }

  handleUserDataAvailable(): void {
    this.isUserInfoAvailable = true;
    this.user = new User(this.userInfoCache);
    this.checkIsMobile();
    if (this.user) {
      this.loading = false;
    }
    this.subscribeToRouterEventNavigationEnd();
    const isPdfGeneration = this.isPdfGeneration();
    if (this.user && this.user.authenticated) {
      if (!isPdfGeneration) {
        this.initializePendo();
      }
      this.localCacheService.storeData("username", this.user.userPrincipalName);
      this.showAnnouncements(this.userInfoCache.UserId);
      let roles = this.userService.getUserRole();
      let engagementPermission = false;
      if (roles) {
        roles.forEach((role) => {
          if (role.PermissionId.toString() === USERROLES.ENGAGEMENT_MESSENGER) {
            engagementPermission = true;
          }
        });
      }
      this.hasEngagementPermission = engagementPermission;
      if (!isPdfGeneration && engagementPermission && !this.isEvaSnowEnabled) {
        let scriptPath = envModule.environment.Engagement_Messenger_Scripts;
        let moduleID = envModule.environment.Engagement_Messenger_ModuleId;
        this.scriptLoader
          .loadScript(scriptPath)
          .then(() => {
            (window as any).SN_CSM_EC.init({
              moduleID: moduleID,
              loadFeature: (window as any).SN_CSM_EC.loadEMFeature(),
              tokenCallBack: () => {
                return Promise.resolve(this.idToken);
              },
            });
            (window as any).SN_CSM_EC.onLogin();
          })
          .catch((error) => {
            console.log("error  -> " + error);
          });
      }
    }
  }
  /**
   * @description this method is trigger when user don't show the subheader section.
   */
  subscribeToRouterEventNavigationEnd() {
    this.hideSubHeader = false;
    this.hideFooter = false;
    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (!(event instanceof NavigationEnd)) {
          return;
        }
        const currentUrl = event.urlAfterRedirects || event.url;
        this.updateOnboardingRouteFlag(currentUrl);
        const routeData = this.getDeepestRouteData();
        this.hideLayout = !!routeData.hideLayout;
        this.hideSubHeader = !!routeData.hideSubHeader;
        this.hideFooter = !!routeData.hideFooter;
        this.globalIdleEnabled =
          routeData["globalIdleEnabled"] != undefined
            ? Boolean(routeData["globalIdleEnabled"])
            : true;
        this.idleSessionService.setWarningUiEnabled(this.globalIdleEnabled);

        // const hideSubHeaderRoutes = [
        //   '/financial-impact',
        //   '/action-recommendations',
        //   '/home',
        //   '/data-visualization-v2',
        //   '/omni-iq',
        //   '/condenseriq',
        //   '/heat-exchanger-iq',
        //   '/rolling-mills-iq',
        //   '/air-separation-iq',
        //   '/caster-systems-iq'
        // ];
        if (
          hideSubHeaderRoutes.some((path) => currentUrl.startsWith(path)) ||
          this.isFbiOnboardingRoute
        ) {
          this.hideSubHeader = true;
        }
        window.scrollTo(0, 0);
      }),
    );
  }

  private updateOnboardingRouteFlag(url?: string): void {
    const resolvedUrl =
      url || this.router.url || window.location.pathname || "";
    this.isFbiOnboardingRoute = resolvedUrl.startsWith(
      "/food-and-beverage-intelligence/onboarding",
    );
    this.isWaterIntelligenceRoute = /^\/water(\/|$|\?)/.test(resolvedUrl);
    this.isChillerIqRoute = resolvedUrl.startsWith("/chiller-iq");
    this.isDCI = resolvedUrl.startsWith("/legionella-test-results") || resolvedUrl.startsWith("/upload-reports-and-plans") || resolvedUrl.startsWith("/coolant-health-iq");
    this.isFnbiRoute = resolvedUrl.startsWith("/food-and-beverage-intelligence") && !this.isFbiOnboardingRoute;
    this.checkIsMobile();
    this.cdref.markForCheck();
  }

  private getDeepestRouteData(): Record<string, unknown> {
    let route = this.activeRoute;

    while (route.firstChild) {
      route = route.firstChild;
    }

    return route.snapshot?.data ?? {};
  }

  showAnnouncements(userId: any) {
    this.chachedAllAnnouncemnets =
      this.localCacheService.fetchData("AllAnnouncemnet");
    this.allannouncement = JSON.parse(this.chachedAllAnnouncemnets);
    if (this.allannouncement && this.allannouncement.length > 0) {
      this.allannouncement.forEach((item) => {
        if (
          this.localCacheService.fetchData(
            "AnnouncemnetStatus" + item.AnnouncementId,
          ) ===
          "Announcemnet" + item.AnnouncementId
        ) {
          item.Isdisable = false;
        }
      });
      this.setPopUpPosition();
    } else {
      this.showPopup(userId);
    }
  }

  ngOnDestroy() {
    // prevent memory leak when component is destroyed
    this.subscriptions.forEach((s) => s.unsubscribe());
    this._destroying$.next();
    this._destroying$.complete();
  }

  showPopup(userId: any) {
    this.allannouncement = [];
    if (userId) {
      // Commenting it for now will have a new call "GetAllAnnouncements" instead of this
      /* this.userService.getLatestAnnouncements(userId).subscribe((response) => {
        if (response && response.length > 0) {
          response.forEach((element) => {
            const dateToday = new Date();
            let endDate = new Date(element.EndDate);
            if (dateToday <= endDate) {
              this.allannouncement.push({
                AnnouncementId: element.Id,
                Message: element.Message,
                EndDate: element.EndDate,
                Isdisable: true,
              });
            }
            this.setPopUpPosition();
            this.localCacheService.storeData(
              'AllAnnouncemnet',
              JSON.stringify(this.allannouncement)
            );
          });
        }
      }); */
    }
  }

  onCloseClick(index: number, announcementId: any) {
    if (announcementId) {
      this.localCacheService.storeData(
        "AnnouncemnetStatus" + announcementId,
        "Announcemnet" + announcementId,
      );
      this.allannouncement.splice(index, 1);
      this.setPopUpPosition();
    }
  }

  onGotItClick(index: number, announcementId: any) {
    let userId = this.userService.userInfo.UserId;
    if (userId && announcementId) {
      this.userService
        .saveAnnouncementStatusForUser(userId, announcementId)
        .subscribe((response) => {
          if (response) {
            this.localCacheService.storeData(
              "AnnouncemnetStatus" + announcementId,
              "Announcemnet" + announcementId,
            );
            this.allannouncement.splice(index, 1);
            this.setPopUpPosition();
          }
        });
    }
  }

  setPopUpPosition() {
    if (this.allannouncement.length > 0) {
      let i = 0,
        x: any,
        y: any,
        z: any;
      x = 130;
      y = this.getWindowWidth();
      z = x + y;
      this.allannouncement.forEach((item) => {
        if (item.Isdisable !== false) {
          if (i === 0) {
            item.top = 0;
          } else if (i === 1) {
            item.top = z;
          } else {
            z = z + y + 130;
            item.top = z;
          }
          i++;
        }
      });
    }
  }

  /**
   * @description Routes where the narrow-window (< 720px) mobile layout is enabled:
   * the DCI routes plus /home. Other routes keep the desktop layout on narrow windows.
   */
  private isMobileScopedRoute(): boolean {
    const url = this.router.url;
    return (
      url.startsWith("/legionella-test-results") ||
      url.startsWith("/upload-reports-and-plans") ||
      url.startsWith("/coolant-health-iq") ||
      url.startsWith("/home")
    );
  }

  /**
   * @description Recomputes the mobile state from device user-agent (all routes) OR viewport
   * width (< 720px, scoped routes only) so the left-nav collapse and full-bleed main content
   * are route-scoped on narrow desktop windows.
   */
  private checkIsMobile(): void {
    const isMobileDevice =
      /android|webos|iphone|ipad|ipod|blackberry|windows phone/.test(
        navigator.userAgent.toLowerCase(),
      );
    this.isMobile =
      isMobileDevice ||
      (this.isMobileScopedRoute() && window.innerWidth < this.mobileBreakpoint);
  }

  @HostListener("window:resize", ["$event"])
  onResize(event: Event) {
    this.checkIsMobile();
    this.setPopUpPosition();
  }

  getWindowWidth() {
    this.screenWidth = window.innerWidth;
    if (this.screenWidth < 540) {
      return (this.height = this.isMobile ? 260 : 200);
    } else if (this.screenWidth < 767 && this.screenWidth > 541) {
      return (this.height = 180);
    } else if (this.screenWidth < 1025 && this.screenWidth > 767) {
      return (this.height = 100);
    } else {
      return (this.height = 80);
    }
  }

  idleReset(): void {
    this.idleSessionService.recordActivity("reset", { force: true });
  }

  /**
   * @description This function is to get the page title from existing sub header module service
   */

  getPagetRoute(): void {
    this.subscriptions.push(
      this.subHeaderService.subheaderLeftSection$.subscribe(async (update) => {
        this.leftSection = await update;
        if (this.leftSection.data) {
          this.isHomePage = false;
        } else {
          this.isHomePage = true;
        }
      }),
    );
  }

  idleStay(): void {
    this.closeIdleModal();
    this.idleSessionService.recordActivity("stay-click", { force: true });
  }

  idleLogout(): void {
    this.closeIdleModal();
    this.idleSessionService.requestLogout("idle");
  }

  handleLoading(loader: boolean) {
    this.showLoader = loader;
  }

  onQuickAction(detail: any): void {
    console.log("Quick action:", detail);
  }

  onWidgetAction(detail: { action: string; data: any }): void {
    console.log("Widget action:", detail);
  }

  initializePendo() {
    if (!envModule.environment.pendoConfig.enabled) {
      return;
    }

    this.userService
      .getUserSettings()
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        next: (settings) => this.initializePendoWithSettings(settings),
        error: () => this.initializePendoWithSettings(),
      });
  }

  private initializePendoWithSettings(settings?: any): void {
    const pendo = (window as any).pendo;
    const isInternalUser = settings?.IsInternalUser ?? this.userInfoCache?.IsInternalUser;
    const account = this.pendoAnalyticsService.account();

    // This function creates visitors and accounts in Pendo
    // You will need to replace <visitor-id-goes-here> and <account-id-goes-here> with values you use in your app
    // Please use Strings, Numbers, or Bools for value types.
    const visitor = {
      id: this.user.profile.mail ?? "", // Required if user is logged in
      email: this.user.profile.mail ?? "", // Recommended if using Pendo Feedback, or NPS Email
      e3d_preferred_culture:
        settings?.Culture ?? this.userInfoCache?.Culture ?? "", // Optional, but recommended
      e3d_persona:
        isInternalUser === undefined
          ? ""
          : isInternalUser
            ? "Internal"
            : "External",
      e3d_cloud_type: envModule.environment.cloudEnv ?? "",
      // full_name:    // Recommended if using Pendo Feedback
      // role:         // Optional
      // You can add any additional visitor level key-values here,
      // as long as it's not one of the above reserved names.
    };

    // Handed over so a later account change can be reported with this same visitor block: Pendo
    // replaces the options it is given instead of merging them, so resending the visitor keeps the
    // metadata above intact while the account is appended.
    this.pendoAnalyticsService.setVisitorOptions(visitor);

    pendo.initialize({
      visitor: visitor,

      // The account holds the customer hierarchy (ContextPointId / ContextPointName) selected in
      // the global search filters. It is only known here when a selection was already restored
      // before Pendo finished initializing; PendoAnalyticsService keeps it up to date afterwards.
      ...(account ? { account: account } : {}),
    });

  }

  private isPdfGeneration(): boolean {
    let pdfGeneration = false;
    const urlHash = new URL(window.location.href).hash;
    const redirectURL =
      new URLSearchParams(urlHash.split("?")[1]).get("returnUrl") ||
      window.location.hash.slice(1);
    if (redirectURL.indexOf("code") < 0) {
      pdfGeneration = redirectURL?.includes("pdfGeneration=true") ?? false;
    }
    return pdfGeneration;
  }

  private openIdleModal(): void {
    if (this.idleModalRef !== null || !this.idleModalContent) {
      return;
    }

    const idleModalOptions: NgbModalOptions = {
      container: ".idle-modal-cont",
      backdrop: "static",
      backdropClass: "idle-modal-backdrop",
      size: "sm",
      keyboard: false,
    };

    this.idleModalRef = this.idleModal.open(
      this.idleModalContent,
      idleModalOptions,
    );
  }

  private closeIdleModal(): void {
    const activeIdleModal = this.idleModalRef;
    if (!activeIdleModal) {
      return;
    }

    activeIdleModal.close();
    this.idleModalRef = null;
  }

  private getIdleMessageKey(phase: IdleSessionPhase): string {
    switch (phase) {
      case "warning":
        return "IDLE_TIMEOUT_POPUP.TIME_OUT_WARNING_IDLE_STATE";
      case "timedOut":
        return "IDLE_TIMEOUT_POPUP.TIMED_OUT_IDLE_STATE_TEXT";
      default:
        return "IDLE_TIMEOUT_POPUP.RESET_IDLE_STATE_TEXT";
    }
  }
}
